import React, { useEffect, useState, useRef, useContext, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Moment from 'moment';
import { Controlled as CodeMirror } from 'react-codemirror2';
import {
    sortBy as _sortBy,
    isEqual as _isEqual,
    isEmpty as _isEmpty,
    cloneDeep as _cloneDeep,
    uniq as _uniq,
    debounce as _debounce,
} from 'lodash';
import clsx from 'clsx';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Select from '@mui/material/Select';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import AddIcon from '@mui/icons-material/Add';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { TagSimpleList } from '../../../components/tags/taglist';
import Tag from '../../../components/tags/tag';
import Push from '../../../components/push/';
import SearchBar from '../../../components/searchbar';
import Validator, { useValidate, checkValidity } from '../../../components/validator';

import PolicyService from '../../../data/policy-service';
import TuningSourceList from '../../../data/tuning-sources';
import DataContext from '../../../data/data-context';
import MetadataService from '../../../data/metadata-service';
import CMCsService from '../../../data/cmcs-service';
import Auth, { User, ProfileContext } from '../../../data/auth-service';

import useWindowSize from '../../../libraries/usewindowsize';
import Helpers from '../../../libraries/helpers';
import Animations from '../../../libraries/animations';

const useStyles = makeStyles(theme => ({
    formControl: {
        margin: theme.spacing(1),
        width: '100%',
    },
    buttonGroup: {
        '& button': {
            float: 'right',
            marginLeft: theme.spacing(1),
        },
        '& label': {
            float: 'right',
            marginLeft: theme.spacing(1),
        },
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(1),
    },
    hideFileInput: {
        display: 'none',
    },
    sourcesContainer: {
        maxHeight: '300px',
        overflowY: 'scroll',
        // '&::-webkit-scrollbar': {
        //     display: 'none',
        // },
        // '-ms-overflow-style': 'none',
        // scrollbarWidth: 'none',
        transition: 'width 500ms',
        position: 'relative',
    },
    select: {
        width: '100%',
    },
    codeContainer: {
        height: '383px',
        marginBottom: '-8px',
        '& .react-codemirror2': {
            height: '100%',
        },
        '& .CodeMirror': {
            height: '100%',
        }
    },
    ...Animations.Push(theme),
    warningBox: {
        border: `1px solid ${theme.palette.warning.main}`,
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
    },
    regexButton: {
        padding: '0px',
        minWidth: '30px',
        borderTopLeftRadius: '0px',
        borderBottomLeftRadius: '0px',
    },
    tooltipLightFix: {
        color: '#FFF',
    },
}));

const emptyFilter = {
    note: '',
    filter: {
        filter: '',
        action: [{ type: 'forget', value: null }],
        scope: ['in'],
        target: ['alert'],
    }
};

const formatSource = (s) => {
    return s.toLowerCase().replaceAll(' ', '_');
};

const defaultPositions = {
    Name: 1,
    Target: 2,
    Scope: 3,
    Action: 4,
    Tags: 5,
    Created: 6,
    Updated: 7,
    Enabled: 8,
};

const DataTuning = () => {
    const classes = useStyles();
    const theme = useTheme();
    const location = useLocation();
    const params = useParams();
    const tagRef = useRef(null);
    const [tuningData, setTuningData] = useState({
        count: 0,
        name: '',
        type: '',
        data: {
            filters: []
        },
    });
    const [, setTuningTable] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState(_cloneDeep(emptyFilter));
    const [multiSelect, setMultiSelect] = useState([]);
    const [create, setCreate] = useState(false);
    const [sourceList, setSourceList] = useState([]);
    const [sources, setSources] = useState(false);
    const [tuningSources, setTuningSources] = useState([]);
    const [edit, setEdit] = useState(false);
    const [showTags, setShowTags] = useState(false);
    // const [tagging, setTagging] = useState(false);
    const [mode, setMode] = useState('data_tags');
    const [updatedSources, setUpdatedSources] = useState();
    const [original, setOriginal] = useState();
    const [originalSources, setOriginalSources] = useState([]);
    const [changes, setChanges] = useState(false);
    // const [changeSources, setChangeSources] = useState(false);
    const [changeSourcesPush, setChangeSourcesPush] = useState(false);
    const { height } = useWindowSize();
    const tuningForm = useValidate();
    const [editor, setEditor] = useState(null);
    const tableAPIRef = useApiRef();
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const { tags, upsertTag } = useContext(DataContext);
    const [columnWidth, setColumnWidth] = useState({});
    const [columnsOrder, setColumnsOrder] = useState(defaultPositions);
    const [pushing, setPushing] = useState(false);
    const [tableHeight, setTableHeight] = useState(500);
    const [search, setSearch] = useState('');
    const [searchData, setSearchData] = useState(null);
    const [searchField, setSearchField] = useState('name');
    const [searchFieldAnchor, setSearchFieldAnchor] = useState(null);
    const [searchRegex, setSearchRegex] = useState(true);
    const mdPerms = Auth.permissions.get('Metadata');
    const gatorPerms = Auth.permissions.get('DataTuningPolicy');
    const [readonly, setReadonly] = useState(!gatorPerms.write);

    const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

    useEffect(() => {
        getTunings();
        getSeedGator();

        mdPerms.read && getTuningSourceList();

        const p = getProfile('datatuning');
        setColumnWidth(p.widths || {});
        setColumnsOrder(p.columns || defaultPositions);
        setTableHeight(Math.max(height - 200, 400));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getProfile, height]);

    useEffect(() => {
        setTuningTable(true);
        getTuningSources();
        if (tuningData) {
            setChanges(!_isEqual(tuningData, original));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tuningData]);

    // useEffect(() => {
    //     if (editor) {
    //         setTimeout(() => {
    //             editor.refresh();
    //         });
    //     }
    // }, [editor]);

    const getTunings = () => {
        PolicyService.getPolicy('data_tags', params.policy).then(res => {
            if (res.data && res.data.filters) {
                res.data.filters = res.data.filters.map((v) => {
                    return { ...v, id: Helpers.UniqueID() };
                });
            } else if (!res.data.filters) {
                res.data.filters = [];
            }
            setOriginal(_cloneDeep(res));
            setTuningData(res);
            setTuningTable(true);
            setReadonly(readonly || (gatorPerms.override ? false : (!MC2CHECK && (res.source === 'mc2' || location.pathname.includes('.mc2')))));
        }, err => {
            if (typeof err === 'string') {
                toast.error(err);
            }
        });
    };

    // alert/mtdt => tune - takes you here with a seed gator
    const getSeedGator = () => {
        const routeQuery = new URLSearchParams(location.search);
        if (!routeQuery.has('gator')) return;

        const seedGator = {
            note: routeQuery.get('note'),
            filter: {
                filter: routeQuery.get('gator'),
                action: routeQuery.getAll('action').map(a => ({ type: a, value: null })),
                scope: routeQuery.getAll('scope') || [],
                target: routeQuery.getAll('target'),
            }
        };

        setCreate(true);
        setSources(false);
        setEdit(false);
        setSelectedFilter(seedGator);
    };

    const getTuningSources = () => {
        if (tuningData && tuningData.data.filters.length) {
            let s = tuningData.data.filters[tuningData.data.filters.findIndex(v => v.note === 'bricata-default')];
            if (s) {
                s = s.filter.filter.replace('NOT (bricata:event_source IN [\'', '').replace('\'])', '').replaceAll("'", '');
                if (s) {
                    s = s.split(',').map(v => v.trim()).filter(v => v);
                    setOriginalSources(_cloneDeep(s));
                    setUpdatedSources(_cloneDeep(s));
                    setTuningSources(s);
                } else {
                    setOriginalSources([]);
                    setUpdatedSources([]);
                    setTuningSources([]);
                }
            }
        }
    };

    const getTuningSourceList = () => {
        if (MC2CHECK) {
            CMCsService.list().then(cmcs => {
                const observedPromises = (cmcs || []).map(cmc => MetadataService.getObserved({ name: cmc.name }));
                return Promise.allSettled(observedPromises);
            }).then(results => {
                let list = _cloneDeep(TuningSourceList);
                const sourceKeys = _uniq((results || []).reduce((acc, result) => {
                    if (result.status !== 'fulfilled') return acc;

                    if (Array.isArray(result.value)) {
                        return [...acc, ...result.value];
                    }
                    if (result.value && typeof result.value === 'object') {
                        return [...acc, ...Object.keys(result.value)];
                    }
                    return acc;
                }, []));

                const observed = sourceKeys.map((k) => {
                    return {
                        name: k.replaceAll('_', ' ').toUpperCase(),
                        description: '',
                    };
                });

                observed.forEach(v => {
                    if (!list.filter(x => x.name === v.name).length) {
                        list.push(v);
                    }
                });

                list = _sortBy(list, ['name']);
                setSourceList(list);
            }, () => {
                setSourceList(TuningSourceList);
            });
        } else {
            MetadataService.getObserved().then(res => {
                let list = _cloneDeep(TuningSourceList);
                let sourceKeys = [];
                if (Array.isArray(res)) {
                    sourceKeys = res;
                } else if (res && typeof res === 'object') {
                    sourceKeys = Object.keys(res);
                } else {
                    setSourceList(TuningSourceList);
                    return;
                }
                const observed = sourceKeys.map((k) => {
                    return {
                        name: k.replaceAll('_', ' ').toUpperCase(),
                        description: '',
                    };
                });
                observed.forEach(v => {
                    if (!list.filter(x => x.name === v.name).length) {
                        list.push(v);
                    }
                });
                list = _sortBy(list, ['name']);
                setSourceList(list);
            }, () => {
                setSourceList(TuningSourceList);
            });
        }
    };

    const buildSources = () => {
        const t = { ...tuningData };
        let idx = t.data.filters.findIndex(v => v.note === 'bricata-default');

        if (idx === -1) {
            t.data.filters.push({
                note: 'bricata-default',
                filter: {
                    filter: '',
                    action: [{ type: 'forget', value: null }],
                    scope: ['cmc'],
                    target: ['metadata'],
                }
            });
            idx = t.data.filters.findIndex(v => v.note === 'bricata-default');
        }
        if (tuningSources.length) {
            t.data.filters[idx].filter.filter = "NOT (bricata:event_source IN ['" + updatedSources.join("','") + "'])";
        } else {
            t.data.filters[idx].filter.filter = '';
        }
        return t;
    };

    const handleUpdateSource = (name) => {
        const t = [...tuningSources];
        if (t.indexOf(formatSource(name)) !== -1) {
            t.splice(t.indexOf(formatSource(name)), 1);
        } else {
            t.push(formatSource(name));
        }
        setUpdatedSources(t);
        setTuningSources(t);
    };

    const handleSendAll = () => {
        if (tuningSources && tuningSources.length) {
            setTuningSources([]);
        } else if (updatedSources && updatedSources.length) {
            setTuningSources(updatedSources);
        } else {
            setTuningSources(['conn']);
        }
    };

    const handleCancelSources = () => {
        setTuningSources(_cloneDeep(originalSources));
        setSources(false);
    };

    const handleCreate = () => {
        tableAPIRef.current.selectRow();

        setCreate(!create);
        setSources(false);
        setEdit(false);
        setSelectedFilter(_cloneDeep(emptyFilter));
        // setTimeout(() => {
        //     editor.refresh();
        // });
    };

    const handleSourceOpen = () => {
        setSources(true);
        setCreate(false);
    };

    const handleSource = () => {
        setSources(false);
        setChangeSourcesPush(!_isEqual(tuningSources, originalSources));
    };

    const handleSavePolicy = () => {
        PolicyService.updatePolicy('data_tags', params.policy, buildSources()).then(() => {
            getTunings();
            setChangeSourcesPush(false);
            setSources(false);
            setCreate(false);
            toast.success('Policy saved successfully');
        }, err => {
            toast.error('Unable to save policy');
            console.error(err);
        });
    };

    const handlePushPolicy = () => {
        setMode('push');
    };

    const handleDeployPolicy = (sensorList) => {
        setPushing(true);
        PolicyService.updatePolicy('data_tags', params.policy, buildSources()).then(() => {
            getTunings();
            setChangeSourcesPush(false);
            setMode('data_tags');
            setSources(false);
            setCreate(false);
            toast.success('Policy saved successfully');

            if (sensorList.length || Object.keys(sensorList).length) {
                PolicyService.assignPolicy('data_tags', params.policy, sensorList).then(() => {
                    toast.success('Policy pushed to new sensor(s) successfully');
                }, err => {
                    if (typeof err === 'string') {
                        toast.error('Pushing policy to new sensor(s) failed : ' + err);
                    } else {
                        toast.error(err.message + ', ' + err.error);
                    }
                });
            }
        }, err => {
            if (err) {
                toast.error(err.message + ': ' + err.error);
            }
        }).finally(() => { setPushing(false) });
    };

    const handleModeChange = (m) => {
        setMode(m);
    };

    const handleRevertPolicy = () => {
        getTunings();
        mdPerms.read && getTuningSourceList();
        getTuningSources();
        setChanges(false);
        // setChangeSources(false);
        setChangeSourcesPush(false);
    };

    const handleSelectFilter = (s) => {
        tuningForm.set({});
        // defense against the dark arts of null values that can't be replaced with merge
        if (!s.row || !s.row.filter) s.row = _cloneDeep(emptyFilter);
        if (!s.row.filter.scope) s.row.filter.scope = [];
        if (!s.row.filter.action) s.row.filter.action = [];
        if (!s.row.filter.target) s.row.filter.target = [];

        setSelectedFilter(_cloneDeep(s.row));
    };

    const handleMultiSelect = (s) => {
        if (s && s.length === 1 && s[0] === undefined) {
            setMultiSelect([]);
        }
        setMultiSelect(s);
    };

    const handleEnable = (e, d) => {
        e.stopPropagation();
        const t = { ...tuningData };
        t.data.filters[t.data.filters.findIndex(v => v.id === d.id)].enabled = e.target.checked;
        setTuningData(t);
    };

    const handleEditFilter = () => {
        setCreate(true);
        setEdit(true);
        // setTimeout(() => {
        //     editor.refresh();
        // });
    };

    const handleDeleteFilter = () => {
        setTuningTable(false);
        const t = { ...tuningData };
        multiSelect.forEach((id) => {
            t.data.filters.splice(t.data.filters.findIndex(v => v.id === id), 1);
        });
        setTuningData(t);
        setMultiSelect([]);
        runSearch(search);
    };

    const handleInfo = () => {
        window.open('/support/filter_reference');
    };

    const handleUpdateDescription = (e, validID, valid) => {
        tuningForm.set({ ...tuningForm.get(), [validID]: valid });

        setSelectedFilter({
            ...selectedFilter,
            note: e.target.value,
        });
    };

    const doubleIncExc = (target, quan, exc, inc1, inc2) => {
        if (target.name === exc) {
            quan = [exc];
        } else {
            if (quan.includes(exc) || quan.findIndex(v => v.type === exc) !== -1) {
                quan = [target.name];
            } else {
                target.checked ? quan.push(target.name) : quan.splice(quan.indexOf(target.name), 1);
            }
        }

        if (!quan.length) {
            target.name === inc1 ? quan.push(inc2) : quan.push(inc1);
        }

        return quan;
    };

    const handleUpdateTarget = (e) => {
        const f = { ...selectedFilter };
        f.filter.target = doubleIncExc(e.target, f.filter.target, '', 'alert', 'metadata');
        f.updated = Moment().utc().toISOString();
        setSelectedFilter(f);
    };

    const handleUpdateAction = (e) => {
        const f = { ...selectedFilter };
        const forget = f.filter.action.findIndex(v => v.type === 'forget');
        const severity = f.filter.action.findIndex(v => v.type === 'severity');
        const tag = f.filter.action.findIndex(v => v.type === 'tag');

        if (e.target.name === 'tag') {
            if (forget !== -1) {
                f.filter.action = [{ type: 'tag', value: [] }];
            } else if (tag !== -1 && severity !== -1) {
                f.filter.action.splice(tag, 1);
            } else if (tag !== -1 && severity === -1) {
                f.filter.action = [{ type: 'severity', value: 1 }];
            } else if (tag === 1 && severity !== -1) {
                f.filter.action.push({ type: 'tag', value: [] });
            } else if (tag === -1 && severity === 0) {
                f.filter.action.push({ type: 'tag', value: [] });
            }
        } else if (e.target.name === 'severity') {
            if (forget !== -1) {
                f.filter.action = [{ type: 'severity', value: 1 }];
            } else if (severity !== -1 && tag !== -1) {
                f.filter.action.splice(severity, 1);
            } else if (severity !== -1 && tag === -1) {
                f.filter.action = [{ type: 'tag', value: [] }];
            } else if (severity === -1 && tag !== 1) {
                f.filter.action.push({ type: 'severity', value: 1 });
            }
        } else {
            f.filter.action = [{ type: 'forget', value: null }];
        }

        setSelectedFilter(f);
    };

    const handleUpdateScope = (e) => {
        const f = { ...selectedFilter };
        f.filter.scope = doubleIncExc(e.target, f.filter.scope, 'in', 'cmc', 'export');
        setSelectedFilter(f);
    };

    const handleUpdateFilter = (e, d, v) => {
        const f = { ...selectedFilter };
        f.filter.filter = v;
        f.updated = Moment().utc().toISOString();
        setSelectedFilter(f);
    };

    const handleSaveCreate = () => {
        setTuningTable(false);
        const t = { ...tuningData };
        const u = User().username;

        if (edit) {
            t.data.filters[t.data.filters.findIndex(v => v.id === selectedFilter.id)] = {
                ...selectedFilter,
                updated: Moment().utc().toISOString(),
            };
        } else {
            const f = { ...selectedFilter };
            f.created = Moment().utc().toISOString();
            f.created_by = u;
            f.updated = Moment().utc().toISOString();
            f.updated_by = u;
            f.enabled = true;
            f.id = Helpers.UniqueID();
            t.data.filters.push(f);
        }
        setTuningData(t);
        tableAPIRef.current.setSelectionModel([]);
        setSelectedFilter(_cloneDeep(emptyFilter));
        setCreate(false);
        setEdit(false);
        setTuningTable(true);
    };

    const handleCancelCreate = () => {
        tableAPIRef.current.selectRow();
        tuningForm.set({});
        tableAPIRef.current.setSelectionModel([]);
        setSelectedFilter(_cloneDeep(emptyFilter));
        setEdit(false);
        setCreate(false);
    };

    const fileHandler = (file) => {
        const reader = new FileReader();
        return new Promise((resolve, reject) => {
            reader.onload = event => resolve(event.target.result);
            reader.onerror = error => reject(error);
            reader.readAsText(file);
        });
    };

    const handleImportFile = (e) => {
        fileHandler(e.target.files[0]).then(content => {
            try {
                const parsedFilters = JSON.parse(content);
                handleImportFilters(parsedFilters);
                handleImportTags(parsedFilters);
            } catch (err) {
                toast.error(`Failed to import data tuning file: ${err && err.message}`);
            }
        }).catch(err => {
            toast.error('Failed to import data tuning file...');
            console.error(err);
        });
    };

    const handleImportFilters = (im) => {
        setTuningTable(false);
        const t = { ...tuningData };
        if (im && im.filters) {
            im.filters.forEach((v) => {
                if (Object.prototype.hasOwnProperty.call(v, 'action')) {
                    t.data.filters.push({
                        ..._cloneDeep(emptyFilter),
                        filter: {
                            ...v,
                            scope: v.scope ? v.scope : ['in'],
                        },
                        id: Helpers.UniqueID(),
                        scope: v.scope ? v.scope : ['in'],
                        enabled: v.enabled || false,
                    });
                } else {
                    const f = tuningData.data.filters.filter(x => _isEqual(x.filter, v.filter));
                    if (!f.length) {
                        t.data.filters.push({ ...v, id: Helpers.UniqueID() });
                    }
                }
            });
        } else if (im.length) {
            im.forEach((v) => {
                const f = tuningData.data.filters.filter(x => _isEqual(x.filter, v.filter));
                if (!f.length) {
                    t.data.filters.push({ ...v, id: Helpers.UniqueID() });
                }
            });
        }
        setTuningData(t);
        setTuningTable(true);
        toast.success('Data tuning file imported successfully');
    };

    const handleImportTags = (im) => {
        const tagNames = Object.keys(tags) || [];

        if (im && im.filters) {
            im.filters.reduce((a, c) => {
                let t = [];
                if (Object.prototype.hasOwnProperty.call(c, 'action')) {
                    t = c.action.filter(v => v.type === 'tag').reduce((b, d) => {
                        return b.concat(d.value);
                    }, []);
                } else {
                    t = c.filter.action.filter(v => v.type === 'tag').reduce((b, d) => {
                        return b.concat(d.value);
                    }, []);
                }
                return _uniq(a.concat(t));
            }, []).forEach((v) => {
                if (!tagNames.includes(v)) {
                    upsertTag({ name: v });
                }
            });
        } else if (im.length) {
            im.reduce((a, c) => {
                const t = c.filter.action.filter(v => v.type === 'tag').reduce((b, d) => {
                    return b.concat(d.value);
                }, []);
                return _uniq(a.concat(t));
            }, []).forEach((v) => {
                if (!tagNames.includes(v)) {
                    upsertTag({ name: v });
                }
            });
        }
    };

    const handleExportFile = () => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([JSON.stringify(tuningData.data.filters)], { type: 'text/plain' }), 'data_tags.json');
        a.target = '_blank';
        a.download = 'data_tags.json';
        a.click();
    };

    const handleShowTags = () => {
        setShowTags(!showTags);
    };

    const handleTagSelect = (tag) => {
        const s = { ...selectedFilter };
        if (s.filter.action.filter(v => v.type === 'tag').length) {
            if (!s.filter.action.filter(v => v.type === 'tag')[0].value.includes(tag)) {
                s.filter.action.filter(v => v.type === 'tag')[0].value.push(tag);
            } else {
                s.filter.action.filter(v => v.type === 'tag')[0].value.splice(s.filter.action.filter(v => v.type === 'tag')[0].value.indexOf(tag), 1);
            }
        }
        setSelectedFilter(s);
    };

    const handleSelectSeverity = (e) => {
        const f = { ...selectedFilter };
        f.filter.action.filter(v => v.type === 'severity')[0].value = e.target.value;
        setSelectedFilter(f);
    };

    const handleColumnResize = params => {
        // tableAPIRef.current.getAllColumns() returns column widths BEFORE resize event
        setColumnWidth(current => {
            return {
                ...current,
                [params.colDef.headerName]: params.width,
            };
        });
    };

    useEffect(() => {
        if (!_isEmpty(columnWidth)) {
            updateProfile('datatuning', {
                ...getProfile('datatuning'),
                widths: columnWidth,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnWidth]);

    const handleColumnReorder = () => {
        // tableAPIRef.current.getAllColumns() returns column order after reorder event (wtf)
        setTimeout(() => {
            const order = tableAPIRef.current.getAllColumns().reduce((a, c, i) => ({
                ...a,
                [c.headerName]: i,
            }), {});

            setColumnsOrder(order);

            updateProfile('datatuning', {
                ...getProfile('datatuning'),
                columns: order,
            });
        });
    };

    const _handleSearch = ({ search }) => {
        setSearch(search || '');
    };

    const handleSearch = _debounce(_handleSearch, 750);

    useEffect(() => {
        runSearch(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, searchRegex, searchField]);

    const runSearch = (search) => {
        if (!search) {
            setSearchData(null);
            return;
        }

        let r;
        let rValid = true;
        if (searchRegex) {
            try {
                r = new RegExp(search, 'i');
            } catch (e) {
                rValid = false;
                console.error(e);
            }
        }

        setSearchData(tuningData.data.filters.map(v => {
            if (searchField === 'filter') {
                if (searchRegex && rValid) return r.test(v.filter.filter) ? v : null;
                return v.filter.filter.includes(search) ? v : null;
            }
            if (searchRegex && rValid) return r.test(v.note) ? v : null;
            return v.note.includes(search) ? v : null;
        }).filter(x => x));
    };

    const handleOpenSearchFieldMenu = (e) => {
        setSearchFieldAnchor(e.target);
    };

    const handleCloseSearchFieldMenu = () => {
        setSearchFieldAnchor(null);
    };

    const handleSearchField = (searchField) => {
        setSearchField(searchField);
        handleCloseSearchFieldMenu();
    };

    // const handleToggleRegex = () => {
    //     setSearchRegex(!searchRegex);
    // };

    const codemirrorOptions = {
        lineNumbers: true,
        theme: theme.codeMirror.theme,
        mode: 'gator',
    };

    const tableRenderers = {
        title: d => { return <div>{d.row.note}</div> },
        created: d => { return <div>{Moment(d.row.created).format(theme.date.long)}</div> },
        updated: d => { return <div>{Moment(d.row.updated).format(theme.date.long)}</div> },
        action: d => {
            const a = [];
            d.row.filter.action.forEach((v) => { a.push(v.type) });
            return <div>{a.join(', ')}</div>;
        },
        tags: d => {
            const tags = [];
            d.row.filter.action.forEach((v) => {
                if (v.type === 'tag') {
                    v.value.forEach((t) => { tags.push(t) });
                }
            });
            return tags.map((v) => {
                return <Tag key={'tag-' + v} tag={v} />;
            });
        },
        target: d => {
            return <div>{d.row.filter.target ? d.row.filter.target.join(', ') : null}</div>;
        },
        scope: d => {
            return <div>{d.row.filter.scope ? d.row.filter.scope.join(', ') : null}</div>;
        },
        enabled: d => {
            return (
                <FormControlLabel
                    label=""
                    control={
                        <Checkbox
                            disabled={readonly}
                            checked={d.row.enabled}
                            onClick={(e) => { handleEnable(e, d.row) }}
                        />
                    }
                />
            );
        }
    };

    const getColumnWidth = useCallback((column, flex) => {
        return columnWidth[column] ? { width: columnWidth[column] } : {};
    }, [columnWidth]);

    const getColumns = () => {
        const c = [
            {
                sortable: true,
                ...{ ...getColumnWidth('Name', 1) },
                type: 'string',
                disableColumnMenu: true,
                hidden: false,
                headerName: 'Name',
                field: 'note',
                renderCell: tableRenderers.title,
                valueGetter: v => v.row.note,
            },
            {
                sortable: true,
                ...{ ...getColumnWidth('Target', 1) },
                type: 'string',
                disableColumnMenu: true,
                hidden: false,
                headerName: 'Target',
                field: 'filter.target',
                renderCell: tableRenderers.target,
                valueGetter: v => v.row.filter.target,
                sortComparator: (v1, v2) => {
                    if (v1.length && v2.length) {
                        if (v1[0] < v2[0]) return -1;
                        if (v1[0] > v2[0]) return 1;
                        if (v1[0] === v2[0] && v1.length > v2.length) return 1;
                        if (v1[0] === v2[0] && v1.length < v2.length) return -1;
                        return 0;
                    }
                },
            },
            {
                sortable: true,
                ...{ ...getColumnWidth('Scope', 0.6) },
                type: 'string',
                disableColumnMenu: true,
                hidden: false,
                headerName: 'Scope',
                field: 'scope',
                renderCell: tableRenderers.scope,
                valueGetter: v => v.row.filter.scope,
                sortComparator: (v1, v2) => {
                    if (v1.length && v2.length) {
                        if (v1[0] < v2[0]) return -1;
                        if (v1[0] > v2[0]) return 1;
                        return 0;
                    }
                },
            },
            {
                sortable: true,
                ...{ ...getColumnWidth('Action', 0.5) },
                type: 'string',
                disableColumnMenu: true,
                hidden: false,
                headerName: 'Action',
                field: 'filter.action',
                renderCell: tableRenderers.action,
                valueGetter: v => v.row.filter.action[0].type,
            },
            {
                sortable: true,
                ...{ ...getColumnWidth('Tags', 1) },
                type: 'string',
                disableColumnMenu: true,
                hidden: false,
                headerName: 'Tags',
                field: 'filter.tags',
                renderCell: tableRenderers.tags,
                valueGetter: v => v.row.filter.action[0].value,
            },
            {
                sortable: true,
                ...{ ...getColumnWidth('Created', 1) },
                type: 'string',
                disableColumnMenu: true,
                hidden: false,
                headerName: 'Created',
                field: 'create',
                renderCell: tableRenderers.created,
                valueGetter: v => v.row.created,
            },
            {
                sortable: true,
                ...{ ...getColumnWidth('Updated', 1) },
                type: 'string',
                disableColumnMenu: true,
                hidden: false,
                headerName: 'Updated',
                field: 'updated',
                renderCell: tableRenderers.updated,
                valueGetter: v => v.row.updated,
            },
            {
                sortable: true,
                ...{ ...getColumnWidth('Enabled', 0.6) },
                type: 'string',
                disableColumnMenu: true,
                hidden: false,
                headerName: 'Enabled',
                field: 'enabled',
                renderCell: tableRenderers.enabled,
                sortComparator: (v1, v2) => {
                    return Boolean(v1) - Boolean(v2);
                },
            },
        ];

        c.sort((a, b) => {
            return (columnsOrder[a.headerName] || 0) - (columnsOrder[b.headerName] || 0);
        });

        return c;
    };

    return (
        <main>
            <Grid container spacing={2}>
                <Grid container item xs={12} style={{ display: mode === 'push' ? 'none' : 'flex' }}>
                    <Grid item xs={4}>
                        <Box display="flex">
                            <SearchBar
                                immediate
                                value={search}
                                onSearch={handleSearch}
                                searchservice={{ searchPlaceholder: 'Search data tuning rules' }}
                            />
                            {/* <Tooltip classes={{ tooltip: classes.tooltipLightFix }} title={`${searchRegex ? 'Disable' : 'Enable'} regex search`}><Button
                                className={classes.regexButton}
                                variant={searchRegex ? 'contained' : 'outlined'}
                                color="primary"
                                onClick={handleToggleRegex}
                            >.*</Button></Tooltip> */}
                            <Box ml={1}>
                                <Button variant="contained" onClick={handleOpenSearchFieldMenu}><ExpandMoreIcon /> {searchField[0].toUpperCase() + searchField.substring(1)}</Button>
                            </Box>
                            <Menu
                                anchorEl={searchFieldAnchor}
                                onClose={handleCloseSearchFieldMenu}
                                open={Boolean(searchFieldAnchor)}
                            >
                                <MenuItem onClick={() => { handleSearchField('name') }}>Name</MenuItem>
                                <MenuItem onClick={() => { handleSearchField('filter') }}>Filter</MenuItem>
                            </Menu>
                        </Box>
                    </Grid>
                    <Grid item xs={8} className={classes.buttonGroup}>
                        {!MC2CHECK && tuningData.source === 'mc2' && <Box mb={1} className={classes.warningBox} display="inline-block">
                            You cannot modify or push a policy assigned by an MC2.
                        </Box>}
                        {!readonly && <Button
                            variant="contained"
                            color="secondary"
                            aria-label="revert"
                            onClick={handleRevertPolicy}
                            disabled={!MC2CHECK && tuningData.source === 'mc2'}
                        >Revert</Button>}
                        {gatorPerms.admin && <Button
                            variant="contained"
                            color="primary"
                            aria-label="push"
                            onClick={handlePushPolicy}
                            disabled={(!MC2CHECK && tuningData.source === 'mc2') || sources || create}
                            className={clsx({
                                [classes.pushAnimation]: (!(!MC2CHECK && tuningData.source === 'mc2') && changes) || changeSourcesPush,
                            })}
                        >Push</Button>}
                        {!readonly && <Button
                            variant="contained"
                            color="primary"
                            aria-label="save"
                            onClick={handleSavePolicy}
                            disabled={(!MC2CHECK && tuningData.source === 'mc2') || sources || create}
                            className={clsx({
                                [classes.pushAnimation]: (!(!MC2CHECK && tuningData.source === 'mc2') && changes) || changeSourcesPush,
                            })}>
                            Save
                        </Button>}
                        {!readonly && <Button
                            variant="contained"
                            color="primary"
                            aria-label="export"
                            onClick={handleExportFile}
                            disabled={!MC2CHECK && tuningData.source === 'mc2'}>
                            <ImportExportIcon /> Export
                        </Button>}
                        {!readonly && <label htmlFor="tuning-import-file">
                            <Button
                                variant="contained"
                                aria-label="import"
                                color="primary"
                                component="div"
                                disabled={!MC2CHECK && tuningData.source === 'mc2'}>
                                <ImportExportIcon /> Import
                            </Button>
                        </label>}
                        {!readonly && <Button
                            variant="contained"
                            aria-label="create"
                            onClick={handleCreate}
                            disabled={create || (!MC2CHECK && tuningData.source === 'mc2')}>
                            <AddIcon /> Create
                        </Button>}
                        {(!readonly && mdPerms.write) && <React.Fragment><Button
                            variant="contained"
                            aria-label="sources"
                            onClick={handleSourceOpen}>
                            <DescriptionIcon /> Sources
                        </Button>
                        <input
                            id="tuning-import-file"
                            className={classes.hideFileInput}
                            aria-label="handle import file"
                            type="file"
                            onClick={(e) => { e.target.value = null }}
                            onChange={handleImportFile}
                        /></React.Fragment>}
                    </Grid>
                </Grid>
                {sources && <Grid item xs={12}>
                    <Box>
                        <Accordion expanded={sources}>
                            <AccordionDetails>
                                <Box>
                                    <Box p={3}>
                                        <Box className={classes.sourcesContainer}>
                                            <Grid container>
                                                {sourceList.map(v => {
                                                    return (
                                                        <Grid item xs={3} key={v.name + '-' + v.id}>
                                                            <ListItem>
                                                                <ListItemIcon>
                                                                    <Checkbox
                                                                        aria-label="update source"
                                                                        checked={(tuningSources || []).includes(formatSource(v.name))}
                                                                        onClick={() => { handleUpdateSource(v.name) }}
                                                                    />
                                                                </ListItemIcon>
                                                                <ListItemText primary={v.name} secondary={v.description}/>
                                                            </ListItem>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </Box>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                        <Box>
                                            <FormControlLabel
                                                label="Send all"
                                                control={
                                                    <Checkbox
                                                        checked={!tuningSources.length}
                                                        onClick={handleSendAll}
                                                    />
                                                }
                                            />
                                        </Box>
                                        <Box display="flex">
                                            <Box ml={2}><Button variant="contained" color="secondary" aria-label="cancel" onClick={handleCancelSources}>Cancel</Button></Box>
                                            <Box ml={2}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    aria-label="ok"
                                                    onClick={handleSource}
                                                    disabled={!MC2CHECK && tuningData.source === 'mc2'}
                                                >Ok</Button>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Grid>}
                {create && <Grid item xs={12}>
                    <Box>
                        <Accordion expanded={create}>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Paper elevation={3}>
                                            <Box p={3}>
                                                <Validator
                                                    errorText="Name cannot be empty"
                                                    fullWidth
                                                    label="Name"
                                                    aria-label="name"
                                                    value={selectedFilter && selectedFilter.note}
                                                    onChange={handleUpdateDescription}
                                                />
                                            </Box>
                                            <Box p={3}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={4}>
                                                        <FormControl>
                                                            <FormLabel>Target</FormLabel>
                                                            <FormControlLabel
                                                                label="Alert"
                                                                aria-label="alert"
                                                                name="alert"
                                                                control={
                                                                    <Checkbox
                                                                        name="alert"
                                                                        checked={selectedFilter && selectedFilter.filter.target.includes('alert')}
                                                                        value={(selectedFilter && selectedFilter.filter.target.includes('alert')) || false}
                                                                        onChange={handleUpdateTarget}
                                                                    />
                                                                }
                                                            />
                                                            <FormControlLabel
                                                                label="Metadata"
                                                                aria-label="metadata"
                                                                control={
                                                                    <Checkbox
                                                                        name="metadata"
                                                                        checked={selectedFilter && selectedFilter.filter.target.includes('metadata')}
                                                                        value={(selectedFilter && selectedFilter.filter.target.includes('metadata')) || false}
                                                                        onChange={handleUpdateTarget}
                                                                    />
                                                                }
                                                            />
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <FormControl>
                                                            <FormLabel>Action</FormLabel>
                                                            <FormControlLabel
                                                                label="Forget"
                                                                aria-label="forget"
                                                                control={
                                                                    <Checkbox
                                                                        name="forget"
                                                                        checked={selectedFilter && !!selectedFilter.filter.action.filter(v => v.type === 'forget').length}
                                                                        value={(selectedFilter && !!selectedFilter.filter.action.filter(v => v.type === 'forget').length) || false}
                                                                        onChange={handleUpdateAction}
                                                                    />
                                                                }
                                                            />
                                                            <FormControlLabel
                                                                label="Tag"
                                                                aria-label="tag"
                                                                control={
                                                                    <Checkbox
                                                                        name="tag"
                                                                        checked={selectedFilter && !!selectedFilter.filter.action.filter(v => v.type === 'tag').length}
                                                                        value={(selectedFilter && !!selectedFilter.filter.action.filter(v => v.type === 'tag').length) || false}
                                                                        onChange={handleUpdateAction}
                                                                    />
                                                                }
                                                            />
                                                            <FormControlLabel
                                                                label="Severity"
                                                                aria-label="severity"
                                                                control={
                                                                    <Checkbox
                                                                        name="severity"
                                                                        checked={selectedFilter && !!selectedFilter.filter.action.filter(v => v.type === 'severity').length}
                                                                        value={(selectedFilter && !!selectedFilter.filter.action.filter(v => v.type === 'severity').length) || false}
                                                                        onChange={handleUpdateAction}
                                                                    />
                                                                }
                                                            />
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <FormControl>
                                                            <FormLabel>Scope</FormLabel>
                                                            <FormControlLabel
                                                                label="In"
                                                                aria-label="in"
                                                                control={
                                                                    <Checkbox
                                                                        name="in"
                                                                        checked={selectedFilter && selectedFilter.filter.scope.includes('in')}
                                                                        value={(selectedFilter && selectedFilter.filter.scope.includes('in')) || false}
                                                                        onChange={handleUpdateScope}
                                                                    />
                                                                }
                                                            />
                                                            <FormControlLabel
                                                                label="CMC"
                                                                aria-label="cmc"
                                                                control={
                                                                    <Checkbox
                                                                        name="cmc"
                                                                        checked={selectedFilter && selectedFilter.filter.scope.includes('cmc')}
                                                                        value={(selectedFilter && selectedFilter.filter.scope.includes('cmc')) || false}
                                                                        onChange={handleUpdateScope}
                                                                    />
                                                                }
                                                            />
                                                            <FormControlLabel
                                                                label="Export"
                                                                aria-label="export"
                                                                control={
                                                                    <Checkbox
                                                                        name="export"
                                                                        checked={selectedFilter && selectedFilter.filter.scope.includes('export')}
                                                                        value={(selectedFilter && selectedFilter.filter.scope.includes('export')) || false}
                                                                        onChange={handleUpdateScope}
                                                                    />
                                                                }
                                                            />
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Paper elevation={3}>
                                                            <Box p={2} display={selectedFilter.filter.action.filter(v => v.type === 'tag').length ? 'block' : 'none'}>
                                                                <Box mb={1}>
                                                                    Tags
                                                                    <IconButton
                                                                        className={classes.buttonRight}
                                                                        ref={tagRef}
                                                                        aria-label="add tags"
                                                                        onClick={handleShowTags}
                                                                        size="large"><AddIcon /></IconButton>
                                                                </Box>
                                                                <TagSimpleList
                                                                    show={showTags}
                                                                    placement='bottom-end'
                                                                    onClickAway={() => setShowTags(false)}
                                                                    anchorEl={tagRef.current}
                                                                    onSelect={t => handleTagSelect(t.name)}
                                                                />
                                                                {/* {selectedFilter.filter.action.filter(v => v.type === 'tag') && */
                                                                    selectedFilter.filter.action.filter(v => v.type === 'tag').map((t, i) => {
                                                                        return t.value.map((v, i) => {
                                                                            return (<Tag
                                                                                key={v + i}
                                                                                tag={v}
                                                                                onUntag={handleTagSelect}
                                                                                untagLabel={'remove'}
                                                                            />);
                                                                        });
                                                                    })}
                                                            </Box>
                                                        </Paper>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Paper elevation={3}>
                                                            <Box p={2} display={selectedFilter.filter.action.filter(v => v.type === 'severity').length ? 'block' : 'none'}>
                                                                Severity
                                                                <Select
                                                                    className={classes.select}
                                                                    value={(selectedFilter.filter.action.filter(v => v.type === 'severity').length &&
                                                                        selectedFilter.filter.action.filter(v => v.type === 'severity')[0].value) || 1
                                                                    }
                                                                    onChange={handleSelectSeverity}
                                                                >
                                                                    <MenuItem value={1}>1</MenuItem>
                                                                    <MenuItem value={2}>2</MenuItem>
                                                                    <MenuItem value={3}>3</MenuItem>
                                                                    <MenuItem value={4}>4</MenuItem>
                                                                </Select>
                                                            </Box>
                                                        </Paper>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Paper elevation={3}>
                                            <Box pt={1} pl={3} pb={3} pr={3}>
                                                <Typography variant="h6">Filter</Typography>
                                                <Box className={classes.codeContainer}>
                                                    <CodeMirror
                                                        value={selectedFilter && selectedFilter.filter.filter}
                                                        onBeforeChange={handleUpdateFilter}
                                                        options={codemirrorOptions}
                                                        editorDidMount={(editor) => { setEditor(editor) }}
                                                    />
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box p={2}>
                                            <Button
                                                className={classes.buttonRight}
                                                ml={1}
                                                variant="contained"
                                                aria-label="save"
                                                color="primary"
                                                onClick={handleSaveCreate}
                                                disabled={!checkValidity(tuningForm.get())}
                                            >{edit ? 'Update' : 'Create'}</Button>
                                            <Button className={classes.buttonRight} ml={1} variant="contained" aria-label="cancel" color="secondary" onClick={handleCancelCreate}>Cancel</Button>
                                            <Button className={classes.buttonRight} ml={1} aria-label="info" onClick={handleInfo}><InfoIcon /></Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Grid>}
            </Grid>
            <Box display={mode === 'push' ? 'block' : 'none'}>
                {gatorPerms.admin && <Push
                    policyName={params.policy}
                    policyType={'data_tags'}
                    onDeploy={handleDeployPolicy}
                    closePush={() => { handleModeChange('data_tags') }}
                    pushing={pushing}
                    permName={'DataTuningPolicy'}
                />}
            </Box>
            <Box style={{ height: tableHeight }} mt={2} display={mode === 'push' ? 'none' : 'block'}>
                <DataGridPro
                    apiRef={tableAPIRef}
                    components={{
                        Header: readonly ? undefined : () => {
                            const rowLength = tableAPIRef.current.getSelectedRows().size;
                            return (
                                <Box p={2}>
                                    <IconButton
                                        disabled={!selectedFilter || (selectedFilter && multiSelect.length !== 1) || !rowLength}
                                        className={classes.buttonRight}
                                        color="primary"
                                        aria-label="edit filter"
                                        onClick={handleEditFilter}
                                        size="large">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        disabled={!selectedFilter || (selectedFilter && !multiSelect.length) || !rowLength}
                                        className={classes.buttonRight}
                                        color="secondary"
                                        aria-label="delete filter"
                                        onClick={handleDeleteFilter}
                                        size="large">
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            );
                        },
                    }}
                    rows={(searchData !== null ? searchData.filter(v => v.note !== 'bricata-default') : tuningData.data.filters.filter(v => v.note !== 'bricata-default')) || []}
                    columns={getColumns()}
                    disableColumnFilter
                    disableColumnMenu
                    onRowClick={handleSelectFilter}
                    onSelectionModelChange={handleMultiSelect}
                    onColumnResize={handleColumnResize}
                    onColumnOrderChange={handleColumnReorder}
                />
            </Box>
        </main>
    );
};

export default DataTuning;
